/**
 * Error Boundary — Browser
 *
 * Native React error boundary (replaces @sentry/react ErrorBoundary). A render
 * crash in the root shell forwards to clientLogger (→ Loki), matching
 * FeatureErrorBoundary — previously this top-level boundary only console.error'd,
 * so a crash above the route subtree left NO server-visible signal.
 */
import { clientLogger } from '@/shared/lib/client-logger';
import { Component, type ReactNode } from 'react';

export class AppErrorBoundary extends Component<
  { fallback: ReactNode; children?: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown) {
    // Console.error → browser DevTools; clientLogger → Loki (via the BFF beacon).
    console.error('[ErrorBoundary]', error);
    clientLogger.error(
      { component: 'AppErrorBoundary', error },
      'Unhandled render error in root boundary'
    );
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
